import React from 'react'
import { useScrollReveal } from '../../../hooks/useScrollReveal.jsx'
import styles from './InfographicsSection.module.css'

const SERVICE_OUTCOMES = [
  {
    title: 'Improve Workflows',
    text: 'Identify slow handoffs, manual reviews, and brittle processes, then replace them with practical automation.'
  },
  {
    title: 'Modernize Technology',
    text: 'Turn legacy systems into maintainable cloud, SaaS, and AI-ready architecture your team can keep shipping.'
  },
  {
    title: 'Deliver Production Solutions',
    text: 'Build secure, observable, cost-aware platforms designed for reliability, scale, and enterprise use.'
  }
]

const PROOF_POINTS = ['Architecture reviews', 'AI workflow automation', 'Cloud and SaaS platforms']

const InfographicsSection = () => {
  const { ref: sectionRef, isVisible } = useScrollReveal({ threshold: 0.12 })

  return (
    <section className={styles.infographics} aria-labelledby="service-infographic-title">
      <div
        ref={sectionRef}
        className={styles.container}
        style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(28px)',
          transition: 'opacity 0.6s ease, transform 0.6s ease'
        }}
      >
        <div className={styles.intro}>
          <div className={styles.copy}>
            <span className={styles.eyebrow}>For startups and software companies</span>
            <h2 id="service-infographic-title" className={styles.title}>
              Better workflows. Stronger systems. Production-ready delivery.
            </h2>
            <p className={styles.description}>
              I help teams turn business and engineering bottlenecks into optimized software systems: cleaner workflows, modern architecture, and enterprise-ready delivery.
            </p>

            <div className={styles.actions}>
              <a
                href="https://calendly.com/inonbaguio/30min"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.primaryAction}
                data-cursor-hover
              >
                Book an intro
                <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <path d="M4 10H16M11 5L16 10L11 15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
              <a href="#services" className={styles.secondaryAction} data-cursor-hover>
                View services
              </a>
            </div>

            <ul className={styles.proofList} aria-label="Service focus areas">
              {PROOF_POINTS.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </div>

          <figure className={styles.visual}>
            <img
              src="/infographics/service-simple-flow.png"
              alt="Simple visual flow showing business workflows moving through automation and architecture into a production cloud platform."
              loading="eager"
              decoding="async"
            />
          </figure>
        </div>

        <div className={styles.outcomes} aria-label="How the service helps">
          {SERVICE_OUTCOMES.map((outcome, index) => (
            <article key={outcome.title} className={styles.outcome}>
              <span className={styles.step}>{String(index + 1).padStart(2, '0')}</span>
              <h3>{outcome.title}</h3>
              <p>{outcome.text}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}

export default InfographicsSection
